/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Block component for ExtFilterControl component
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Eugenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Sergey Mysov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.0.0
 */
import { Select } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { IOptionItemProps } from '@components/common/Select/Select';
import { IPageSubscriberMobileTranslation } from '@modules/internationalization/types';
import LinkBlockServiceUserDenied from '@modules/role/components/LinkBlockServiceUserDenied';

import { ISubscriberMobileExtFilter, SAVED_FILTERS } from '../../types/extFilterTypes';
import {
    ControlBlockStyled,
    DeleteBlockStyled,
    SaveBlockStyled,
    SaveButtonStyled,
    SavedFiltersContainerStyled,
} from '../styled/ExtFilterControlBlockStyled';
import SaveAsButton from './SaveAsButton';

/**
 * @description Control block props
 * @interface IControlBlockProps
 * @property {string} extFilterId - external filter id
 * @property {ISubscriberMobileExtFilter[]} extFilterList - saved external filters
 * @property {IPageSubscriberMobileTranslation} translation - locale
 * @property {Function} onSave - save callback
 */
export interface IControlBlockProps {
    extFilterId: string;
    extFilterList: ISubscriberMobileExtFilter[];
    onChange: (item: IOptionItemProps) => void;
    onSave: (type: string, filter: string) => void;
    translation: IPageSubscriberMobileTranslation['extFilter'];
    isUpdateFilters: boolean;
}

const ExtFilterControlBlock = (props: IControlBlockProps) => {
    const selectedFilters = {
        label: props.translation.savedFilters,
        value: SAVED_FILTERS,
    };
    const options: IOptionItemProps[] = [
        selectedFilters,
        ...props.extFilterList.map((item: ISubscriberMobileExtFilter) => {
            return {
                label: item.name,
                value: item.id,
            };
        }),
    ];
    // get uniq extfilter item by id
    const extFilterItem = props.extFilterList.filter((i: ISubscriberMobileExtFilter) => i.id === props.extFilterId);
    const selectedOption =
        props.extFilterId && extFilterItem.length
            ? {
                  value: props.extFilterId,
                  label: extFilterItem[0].name,
              }
            : undefined;

    const onSaveHandler = () => {
        if (selectedOption) {
            props.onSave('save', selectedOption.value);
        }
    };

    const onDeleteHandler = () => {
        if (selectedOption) {
            props.onSave('delete', selectedOption.value);
        }
    };

    return (
        <ControlBlockStyled data-name="control-block-container">
            <SaveBlockStyled>
                <SavedFiltersContainerStyled>
                    <Select
                        classNamePrefix="ext-filter-"
                        filterButtonSize={DropDownFilterButtonSize.medium}
                        options={options}
                        placeholder={props.translation.savedFilters}
                        selectedOption={selectedOption}
                        onSelect={props.onChange}
                    />
                </SavedFiltersContainerStyled>
                <SaveButtonStyled>
                    <LinkBlockServiceUserDenied disabled={!props.isUpdateFilters} onClick={onSaveHandler}>
                        {props.translation.save}
                    </LinkBlockServiceUserDenied>
                </SaveButtonStyled>

                <LinkBlockServiceUserDenied
                    renderChild={(disabled: boolean) => (
                        <SaveAsButton
                            canSaved={!disabled}
                            translation={props.translation}
                            onCreate={props.onSave}
                        ></SaveAsButton>
                    )}
                />
            </SaveBlockStyled>
            <DeleteBlockStyled>
                {!!props.extFilterId && (
                    <LinkBlockServiceUserDenied onClick={onDeleteHandler}>
                        {props.translation.delete}
                    </LinkBlockServiceUserDenied>
                )}
            </DeleteBlockStyled>
        </ControlBlockStyled>
    );
};

export default ExtFilterControlBlock;
